import type { TriggerEvent } from "floating-vue";
import type { Ref } from "vue";
import { options as floatingVueOptions, Popper, SHOW_EVENT_MAP } from "floating-vue";
import { onBeforeUnmount, onMounted, watch } from "vue";

/**
 * Shared pieces for components that host a floating-vue popper lazily (VTooltip, VDropdown).
 *
 * The host renders the reference content inside its own `div.v-popper` root right away and only
 * creates the renderless Popper + PopperContent pair on the first interaction with that root. The
 * DOM stays identical to floating-vue's own wrapper, so styling and nested poppers keep working.
 */

export type LazyPopperComponent = ReturnType<typeof Popper>;

let lazyPopper: LazyPopperComponent | null = null;

/**
 * One renderless Popper definition shared by every host; floating-vue's factory creates a new component per
 * call, which would otherwise defeat Vue's per-component caches. Created on first use, not when this module
 * loads: in a production build floating-vue sits in its own vendor chunk, which this module can be evaluated
 * before, and floating-vue's exports are not initialized yet at that point.
 */
export function lazyPopperComponent(): LazyPopperComponent {
  lazyPopper ??= Popper();
  return lazyPopper;
}

export type PopperTriggers = TriggerEvent[] | ((triggers: TriggerEvent[]) => TriggerEvent[]) | undefined;

// Same classes floating-vue's ThemeClass mixin puts on a popper root: the theme plus every theme it $extends.
export function popperThemeClasses(theme: string): string[] {
  const themes = [theme];
  let config = floatingVueOptions.themes[theme] ?? {};
  while (config.$extend && !config.$resetCss) {
    themes.push(config.$extend);
    config = floatingVueOptions.themes[config.$extend] ?? {};
  }
  return themes.map(name => `v-popper--theme-${name}`);
}

// Bubbling counterparts of the events floating-vue registers on the target nodes (mouseenter and focus do
// not bubble). Any of these inside a host root mounts the popper.
export const POPPER_ACTIVATION_EVENTS = ["mouseover", "focusin", "touchstart", "pointerdown", "click"] as const;

export type PopperActivationEvent = (typeof POPPER_ACTIVATION_EVENTS)[number];

// Trigger names, not SHOW_EVENT_MAP values: those are read when an event arrives, for the same reason as
// lazyPopperComponent.
const ACTIVATION_TRIGGER: Record<PopperActivationEvent, keyof typeof SHOW_EVENT_MAP> = {
  mouseover: "hover",
  focusin: "focus",
  touchstart: "touch",
  pointerdown: "pointer",
  click: "click",
};

type Activate = (event: Event) => void;

// Hosts waiting for their first interaction, keyed by root element. One set of document listeners serves all
// of them: an overview row holds ~20 hosts, and five listeners per host made addEventListener a visible
// share of mounting a row.
const waitingHosts = new WeakMap<Element, Activate>();
let documentListenersInstalled = false;

// Bubble phase, like the listeners used to sit on the host roots: an event that a descendant stops before it
// leaves the root still does not activate the host.
function onActivationEvent(event: Event): void {
  let node = event.target instanceof Element ? event.target : null;
  while (node !== null) {
    waitingHosts.get(node)?.(event);
    node = node.parentElement;
  }
}

function installDocumentListeners(): void {
  if (documentListenersInstalled || typeof document === "undefined") {
    return;
  }
  documentListenersInstalled = true;
  for (const type of POPPER_ACTIVATION_EVENTS) {
    document.addEventListener(type, onActivationEvent, { passive: true });
  }
}

/**
 * Lets the host's `activate` run for the first activation event inside `rootEl`, without attaching any
 * listener to the root itself. Registration ends as soon as the popper is active or the host unmounts.
 */
export function useLazyPopperActivation(rootEl: Ref<Element | null>, active: Ref<boolean>, activate: Activate): void {
  let registered: Element | null = null;

  function unregister(): void {
    if (registered !== null) {
      waitingHosts.delete(registered);
      registered = null;
    }
  }

  onMounted(() => {
    const root = rootEl.value;
    if (active.value || root === null) {
      return;
    }
    installDocumentListeners();
    waitingHosts.set(root, activate);
    registered = root;
  });

  watch(active, (isActive) => {
    if (isActive) {
      unregister();
    }
  });

  onBeforeUnmount(unregister);
}

export function resolveShowEvents(triggers: TriggerEvent[], showTriggers: PopperTriggers): Set<string> {
  let resolved = triggers;
  if (showTriggers !== undefined && showTriggers !== null) {
    resolved = typeof showTriggers === "function" ? showTriggers(triggers) : showTriggers;
  }
  return new Set(resolved.map(trigger => SHOW_EVENT_MAP[trigger]).filter(Boolean));
}

// Whether floating-vue itself would have shown the popper for this event had its listeners been attached to
// the target nodes from the start. Focus only counts on a target node itself because `focus` does not bubble.
export function activationEventShowsPopper(event: Event, targetNodes: Element[], showEvents: Set<string>): boolean {
  const trigger = ACTIVATION_TRIGGER[event.type as PopperActivationEvent];
  const showEvent = trigger ? SHOW_EVENT_MAP[trigger] : undefined;
  if (!showEvent || !showEvents.has(showEvent)) {
    return false;
  }

  const target = event.target;
  if (!(target instanceof Node)) {
    return false;
  }
  if (event.type === "focusin") {
    return targetNodes.includes(target as Element);
  }
  return targetNodes.some(node => node === target || node.contains(target));
}
